import type { AeoPagePublishable } from "@service/aeo";
// src/oura/pageWorkshop.ts
//
// The data model behind the Page Workshop: one crawled page as the FULL,
// ordered document — every block, tagged with what it is (h1, p, img…) —
// with copy and internal-link recommendations anchored to the exact block
// (and text span) they change.
//
// WHY A FLAT BLOCK LIST AND NOT "SECTIONS". A DOM walk cannot reliably NAME
// sections; it can, trivially, produce an ordered list of tagged blocks. The
// block list is both the better review surface (Surfer-style: read
// everything, gutter says what each block is) and the cheaper contract to
// fill. Real pages are NOT plain text, so the block model carries the shapes
// a crawler actually meets:
//
//   img    with a real `src` when captured (FE renders the actual image,
//          placeholder on failure), `figure: "chart"` for graphics that are
//          data, not decoration (charts extract as alt/aria text only).
//   table  header + rows, verbatim.
//   stat   a big number + label (hero counters). Consecutive stats render
//          as a tile row.
//   quote  a testimonial/pull-quote, with its attribution in `label`.
//   group  consecutive blocks sharing a `group` id are ONE visual component
//          on the real page (a card grid of services/features). The walk
//          detects this from repeated sibling structure; the FE renders the
//          cluster as cards so the reviewer sees what a visitor sees.
//   links  the anchors that ALREADY exist inside a block's text — required
//          so link suggestions never duplicate a link that is already there,
//          and so the reviewer can tell existing links from proposed ones.
//
// WHY THIS IS A FIXTURE TODAY. Two inputs do not exist on the wire, both BE:
//
//   1. PAGE CONTENT. The contract carries `markup: "present" | "missing"` —
//      a check RESULT, not the markup. No content, no document, no anchors.
//   2. THE INTERNAL-LINK GRAPH. Orphans are detected, but nothing says
//      "page A links to page B", which is the input to a link suggestion.
//
// This module is the SHAPE we want the backend to fill, exercised with
// deterministic sample data (stable across reloads; two people see the same
// thing). Swap `getPageWorkshop()` for the real call when it lands; the
// components should not need to change.

/** What a block is. Straight off the DOM walk — no invented labels. */
export type BlockTag =
    | "h1"
    | "h2"
    | "h3"
    | "p"
    | "li"
    | "img"
    | "table"
    | "stat"
    | "quote";

/** An anchor that already exists inside a block's text. */
export interface PageLink {
    /** The exact linked substring of the block's text. */
    text: string;
    /** The real destination, relative or absolute. */
    href: string;
}

export interface PageBlock {
    id: string;
    tag: BlockTag;
    /** The block's text. For `img`, empty (alt carries the label); for
     *  `table`, the caption; for `stat`, the value ("4.9 / 5"). */
    text: string;
    /** `img`: the alt text, shown under the image or in the placeholder. */
    alt?: string;
    /** `img`: the resolved absolute image URL, when the crawler captured
     *  one. The FE renders the real image and falls back to a placeholder
     *  if it fails to load — so a broken src degrades, never breaks. */
    src?: string;
    /** `img` only: "chart" marks a graphic that is data (a chart, a graph).
     *  Charts extract as their alt/aria text — pixels carry no copy. */
    figure?: "photo" | "chart";
    /** `stat`: the caption under the value. `quote`: the attribution. */
    label?: string;
    /** `table` only: the grid, verbatim. */
    table?: { header: string[]; rows: string[][] };
    /** Anchors already present in this block's text. */
    links?: PageLink[];
    /** Consecutive blocks sharing a group id are one visual component on
     *  the page (e.g. "cards:services") and render as a card cluster. */
    group?: string;
}

/** Why a recommendation exists, in the words used with the client later. */
/**
 * Where a proposal's facts came from (ourteam-backend#4836, K-1).
 *
 * ALWAYS PRESENT, and empty is the normal case — most businesses have no
 * documents at all, so an empty array means "this sentence has no source" and
 * renders nothing. It is not an omission and not an empty state.
 *
 * Re-exported from the service type rather than redeclared: a second shape for
 * a verified citation is a second answer to "is this sourced".
 */
export type { Citation } from "@service/aeo/types";

export interface Justification {
    /** The reason the change is needed. Shown to the operator AND reusable
     *  verbatim in a weekly client update, which is why it is a sentence and
     *  not a keyword. */
    reason: string;
    /** What should change as a result. Never a number we cannot measure. */
    expected: string;
}

export interface CopyRecommendation {
    kind: "copy";
    id: string;
    /** The block this change applies to. */
    blockId: string;
    /** The exact text span being replaced — a whole block OR one clause of
     *  it. Anchored to TEXT, never a CSS selector: publishing goes through
     *  the WordPress/Framer content APIs, and selectors rot when themes
     *  change. */
    before: string;
    after: string;
    justification: Justification;
    /** See `Citation`. Absent on a proposal generated before K-1. */
    citations?: import("@service/aeo/types").Citation[];
    /**
     * PREVIEW (Ask Oura): where an approved answer came from, shown as a small
     * label above the card ("From the Answer Plan · approved by Siew Ling").
     * Set only by src/oura/ask/planProposals.ts on the sample business.
     */
    origin?: string;
}

/**
 * Did the proposed phrase actually turn up on the live page?
 *
 * The team's own build sheet warns that phrases captured for linking "may
 * differ by a word or two" from the live wording. That is not an edge case:
 * an anchor that does not match exactly cannot be published, and silently
 * dropping the highlight (what the FE used to do) hides the problem.
 */
export type PhraseMatch = "exact" | "approximate" | "missing";

/** The other end of a two-way link — see `pairsWith`. */
export interface PairedPage {
    url: string;
    title: string;
}

export interface LinkRecommendation {
    kind: "link";
    id: string;
    blockId: string;
    /** The words that become the link. When `requiresNewCopy` is true this
     *  text does not exist on the page yet — `proposedCopy` introduces it,
     *  inserted after `blockId`. */
    anchorText: string;
    targetUrl: string;
    targetTitle: string;
    /**
     * True when the page has no natural place to put this link, so a sentence
     * has to be written to create one. This is the case the brief calls out:
     * a page with nothing suitable to link FROM still needs to be reachable.
     */
    requiresNewCopy: boolean;
    /** The sentence to add, when `requiresNewCopy`. */
    proposedCopy?: string;
    /** `approximate` when the live page says it slightly differently — then
     *  `matchedText` is what is actually there, and what gets linked. */
    match?: PhraseMatch;
    matchedText?: string;
    /**
     * Set when this link should also exist in reverse, on the named page.
     *
     * DELIBERATELY INFORMATIONAL. The return link is generated as an ordinary
     * proposal ON THAT PAGE, so approval and publishing stay strictly
     * page-scoped — no cross-page write, no shared state. This field exists
     * so the reviewer knows the other half is waiting rather than discovering
     * it by accident, which is the failure the build sheet's "make each a
     * two-way link" instruction would otherwise walk into.
     */
    pairsWith?: PairedPage;
    justification: Justification;
    /** See `Citation`. Absent on a proposal generated before K-1. */
    citations?: import("@service/aeo/types").Citation[];
}

/**
 * An existing link that points at the wrong place.
 *
 * Straight from the build sheet: "recruitment automation in Singapore"
 * currently links to the homepage and should link to /cv-screening. Neither a
 * copy change nor a new link — the text stays, the destination moves — so it
 * does not consume the page's new-link budget.
 */
export interface RepointRecommendation {
    kind: "repoint";
    id: string;
    blockId: string;
    /** The linked phrase as it appears on the page today. */
    anchorText: string;
    /** Where it points now. */
    currentUrl: string;
    /** Where it should point. */
    targetUrl: string;
    targetTitle: string;
    justification: Justification;
    /** See `Citation`. Absent on a proposal generated before K-1. */
    citations?: import("@service/aeo/types").Citation[];
}

/** The labels a related-links row may carry. A CLOSED set on purpose: making
 *  it free text would mean storing and versioning a user-edited field, which
 *  is a one-way door we are not opening for a label used three ways. */
export type RelatedLabel = "Related reading" | "Compare" | "Regional guides";

/**
 * A small row of links appended to a page that has no natural phrase to link
 * from. The build sheet is explicit that these pages need "only a tiny
 * 'Related' link row, not rewritten copy" — so this is NOT `requiresNewCopy`,
 * which writes a sentence.
 */
export interface RelatedBlockRecommendation {
    kind: "related";
    id: string;
    /** The row is inserted AFTER this block. */
    blockId: string;
    label: RelatedLabel;
    links: PairedPage[];
    justification: Justification;
    /** See `Citation`. Absent on a proposal generated before K-1. */
    citations?: import("@service/aeo/types").Citation[];
}

export type PageRecommendation =
    | CopyRecommendation
    | LinkRecommendation
    | RepointRecommendation
    | RelatedBlockRecommendation;

/**
 * What has happened to this page, oldest first.
 *
 * Two kinds of event share one timeline because the reviewer reads them as one
 * story: drafts Oura generated (a redraft supersedes the previous one on the
 * backend — ourteam-backend#4003) and changes that actually went live. Keeping
 * publications out of it would leave the history saying a page was drafted
 * four times and never explaining why the copy changed.
 */
export type PageHistoryKind = "drafted" | "redrafted" | "published";

/** The kinds the UI has an icon and label for — the runtime companion to the
 *  union above, which TypeScript trusts but nothing checks against the wire. */
export const KNOWN_PAGE_HISTORY_KINDS: readonly PageHistoryKind[] = [
    "drafted",
    "redrafted",
    "published",
] as const;

export function isKnownPageHistoryKind(kind: string): kind is PageHistoryKind {
    return (KNOWN_PAGE_HISTORY_KINDS as readonly string[]).includes(kind);
}

/**
 * One change inside a history event — what an audit trail has to name.
 *
 * "3 changes published" without saying WHICH three is not auditable: nobody
 * can answer "who changed this sentence, and when" from a count. Each entry
 * therefore carries the specific edits, excluding anything the reviewer
 * dismissed (dismissed proposals never happened, so recording them would make
 * the log read as if they had).
 */
export interface PageHistoryChange {
    kind: "copy" | "link" | "repoint" | "related";
    /** Where on the page, in the reviewer's terms — a heading, not a block id. */
    where: string;
    /** Copy changes: the wording, before and after. */
    before?: string;
    after?: string;
    /** Link changes: the words linked and where they now point. */
    anchorText?: string;
    targetUrl?: string;
    /** Repoints only: where the link used to go. */
    fromUrl?: string;
}

export interface PageHistoryEntry {
    id: string;
    kind: PageHistoryKind;
    /** ISO timestamp. */
    at: string;
    /** What this event actually did. The count shown in the UI is its length,
     *  so a log can never claim more changes than it can name. */
    changes: PageHistoryChange[];
    /** The steering note that produced a redraft. Maps to the BE's
     *  `instructions` field. */
    instruction?: string | null;
    /** Who did it. "Oura" for a scan-generated draft, a person's name for
     *  anything a human asked for or approved — the log answers "who changed
     *  this" as often as "what changed". */
    by: string;
    /** Publications only: where the change was written. */
    via?: string;
}

/** Per-page Search Console numbers. `null` when GSC is not connected. */
export interface PageSearchMetrics {
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
}

/**
 * What KIND of page this is, which is what actually drives internal linking.
 *
 * The build sheet labels every page twice — a funnel tier (top/mid/bottom)
 * and a role (pillar, spoke, money page, geo post…). Only the ROLE changes
 * behaviour: a pillar is supposed to carry six outbound links where an
 * ordinary page carries two or three, and a page with no natural phrase gets
 * a related-links row instead of an inline link. The funnel tier is
 * annotation, so it is deliberately not modelled.
 *
 * Inferred from the URL here; the BE should return it (and may derive it
 * better from the link graph, since a page many topical siblings point at is
 * a pillar).
 */
export type PageRole =
    | "home"
    | "guide"
    | "blog post"
    | "comparison"
    | "conversion"
    | "content page"
    | "product"
    | "utility"
    | "placeholder";

/** The roles a reviewer can pick from, in the order they appear in the menu.
 *  The three set-aside roles come last, after a site's real content roles. */
export const PAGE_ROLES: PageRole[] = [
    "home",
    "guide",
    "blog post",
    "comparison",
    "conversion",
    "content page",
    "product",
    "utility",
    "placeholder",
];

/**
 * The roles the BACKEND's enum accepts (`PAGE_ROLES` in `data/models/aeo.py`).
 * `PATCH /businesses/{id}/pages` validates against it and answers 400
 * `unknown_role` for anything else.
 *
 * All nine now, as of ourteam-backend#4368. `product`, `utility` and
 * `placeholder` were held on the client while the backend enum had only six,
 * which is why a reviewer's set-aside labels never reached anybody else: they
 * were suppressed before the request, mirrored to `localStorage`, and stayed in
 * one browser. The enum is on backend `main`, so they can be sent now.
 *
 * Still an explicit list rather than `PageRole` itself. The next role someone
 * adds to the menu will be a frontend idea before it is a backend one, and the
 * failure this list prevents — sending a value the enum rejects — should stay
 * prevented by default rather than by whoever remembers.
 */
export type BackendPageRole = PageRole;

const BE_PAGE_ROLES: readonly BackendPageRole[] = [
    "home",
    "guide",
    "blog post",
    "comparison",
    "conversion",
    "content page",
    "product",
    "utility",
    "placeholder",
];

export function isBackendRole(role: PageRole): role is BackendPageRole {
    return (BE_PAGE_ROLES as readonly PageRole[]).includes(role);
}

/**
 * Pages that are NOT copy work — the reviewer has said so by labelling them.
 *
 * `product` is a buying page, and the catalogue around it: our SEO/AEO work is
 * copy on the pages that answer questions (home, services, FAQs, articles), and
 * a product page is where someone checks out. `utility` is a page that exists to
 * DO something rather than to answer something — sign in, check out, apply for a
 * job. `placeholder` is a page that should not exist at all: an unpublished stub
 * the crawl reached from the domain, whose fix is removal, not a redraft.
 *
 * All three do exactly one thing: they keep the page out of the working set and
 * out of Oura's picks. None blocks a human from opening the page.
 *
 * **Contact and outlets pages are deliberately NOT here** (product decision,
 * 14 Aug). They look like utility pages and nobody rewrites marketing copy on
 * them, but the question recipe generates branch-location and "where is X"
 * questions, and those pages are what answer them. Setting them aside would
 * stop Oura proposing improvements to the page that answers its own questions.
 */
export function isSetAsideRole(role: PageRole): boolean {
    return role === "product" || role === "utility" || role === "placeholder";
}

/** Wire value → what the UI shows. The wire stays lowercase (it is an enum,
 *  not prose); the interface uses title case like every other label. */
export const ROLE_LABEL: Record<PageRole, string> = {
    home: "Home",
    guide: "Guide",
    "blog post": "Blog Post",
    comparison: "Comparison",
    conversion: "Conversion",
    "content page": "Content Page",
    product: "Product",
    utility: "Utility",
    placeholder: "Placeholder",
};

/**
 * The role menu's two halves, derived from `isSetAsideRole` rather than
 * hand-listed — so adding a role to one side can never leave it out of both, or
 * silently in both.
 */
export const COPY_WORK_ROLES: PageRole[] = PAGE_ROLES.filter((r) => !isSetAsideRole(r));
export const SET_ASIDE_ROLES: PageRole[] = PAGE_ROLES.filter(isSetAsideRole);

/**
 * What each role actually means, in the reviewer's terms.
 *
 * A role is a JUDGEMENT with consequences — three of them take a page out of
 * Oura's picks entirely — and the menu was nine bare nouns. "Content Page" and
 * "Utility" are the two that carried no meaning at all on first read: one is the
 * catch-all everything lands in, the other is a category nobody has a word for.
 *
 * So each line names the KINDS OF PAGE that belong there, not a definition.
 * Someone deciding what a URL is recognises their own site faster in a list of
 * examples than in a sentence about purpose. Kept to a handful each: this reads
 * inside a dropdown, over a table, with a decision waiting.
 *
 * Deliberately written from `roleForUrl` below, so the examples are the paths
 * that genuinely infer that role rather than a plausible-sounding set. If the
 * inference changes, these change with it.
 */
export const ROLE_HINT: Record<PageRole, string> = {
    home: "The front page.",
    guide: "Teaching pages, like /guides or /learn.",
    "blog post": "Dated articles and news, like /blog or /news.",
    comparison: "Compares two options, like “x vs y”.",
    conversion: "Booking, enquiry, pricing, demo, quote.",
    // The catch-all, and the one that has to be clearest: this is where a
    // reviewer's actual copy work lives, so "everything else" is not enough.
    "content page": "Answers a question: services, treatments, FAQs, about. Most copy work is here.",
    product: "A shop item, or the catalogue listing them.",
    // Named by what these pages DO, because no everyday word covers signing in
    // and careers at once, which is exactly why the bare label taught nobody
    // anything.
    utility: "Does a job rather than answers a question: sign in, cart, careers.",
    placeholder: "A stub that should not exist. The fix is deletion.",
};

/** URL-pattern inference — the same rule the BE can start from. */
export function roleForUrl(url: string): PageRole {
    // Guard the INPUT, not just the parse.
    //
    // The try below catches a malformed URL, but `path` then falls back to the
    // argument itself — so anything that is not a string sailed past it and hit
    // `path.toLowerCase()` on the line after, outside the try, as an uncaught
    // TypeError mid-render. A wire type promising `string` is not the same as a
    // string arriving: this fired the first time link-graph rows reached this
    // function, and took the Pages tab down to a white screen.
    if (typeof url !== "string" || url.trim() === "") return "content page";
    let path = url;
    try {
        path = new URL(url.startsWith("http") ? url : `https://${url}`).pathname;
    } catch {
        // Keep the raw string; the tests below are all substring checks.
    }
    const p = path.toLowerCase().replace(/\/+$/, "");
    if (p === "" || p === "/") return "home";
    if (/(^|\/)(guides?|resources|learn)\//.test(p)) return "guide";
    if (/(^|\/)(blog|news|insights)\//.test(p)) return "blog post";
    if (p.includes("product-comparison") || p.includes("-vs-")) return "comparison";
    // A shop URL is the one set-aside role a URL can honestly claim: WooCommerce
    // and Shopify both put every item under a fixed prefix, so `/product/…` is a
    // structural fact about the site, not a guess about the copy. Inferring it
    // keeps a reviewer from hand-labelling a catalogue of forty items.
    //
    // `placeholder` is deliberately NEVER inferred. An unpublished stub looks
    // exactly like a real page from the outside — that is the whole reason the
    // role exists — and a machine that guessed it would hide live pages.
    // The catalogue as well as the item. `/product-category/bundle` and
    // `/product-tag/…` are WooCommerce's own taxonomy prefixes and `/collections/`
    // is Shopify's; they list products, so they are the shop just as much as a
    // single item page is. Longer alternatives lead, because `products?\/` alone
    // does not match `product-category/` and would leave it as a content page.
    if (/(^|\/)(product-category|product-tag|collections|products?|shop|store|produk)\//.test(p))
        return "product";
    // Pages that exist to DO something rather than to answer something: sign in,
    // check out, apply for a job. No copy improvement moves them, because nobody
    // arrives at them from a question.
    //
    // Two deliberate omissions. `contact` and `outlets` are NOT here — see
    // isSetAsideRole. And bare `/jobs` is not either: for a recruiter or a job
    // board it is the money page, and a rule that sets a client's core content
    // aside is worse than one that misses a careers page. `careers` and
    // `vacancy` carry the meaning without the collision.
    if (
        /(^|\/)(my-account|account|login|sign-in|signin|logout|lost-password|reset-password|cart|checkout|basket|wishlist|order-tracking|order-received|careers?|kerjaya|job-vacanc\w*|vacanc(y|ies))($|\/)/.test(p)
    )
        return "utility";
    // `contact-us` reads as the same page as `contact` and was falling through to
    // "content page" purely because the pattern demanded an exact segment.
    if (/(^|\/)(pricing|contact|contact-us|book|booking|demo|signup|sign-up|quote)($|\/)/.test(p))
        return "conversion";
    return "content page";
}

/**
 * How many NEW links a page may take. The build sheet's rule of thumb is two
 * or three "so it reads naturally" — but a pillar links down to every spoke,
 * which is the whole point of a pillar, so the cap is per role. Existing
 * links do not count; repointing a wrong link does not count either.
 */
export function linkBudgetForRole(role: PageRole): number {
    return role === "guide" || role === "home" ? 6 : 3;
}

export interface PageWorkshop {
    /**
     * Whether Oura can write to this page, per change kind (#3575, BE #4646).
     *
     * Answered on the page READ so a reviewer learns it before working through
     * nine proposals, rather than at the moment they press Publish. Absent on a
     * backend that predates it — the surfaces then say nothing, as before.
     */
    publishable?: AeoPagePublishable | null;
    url: string;
    title: string;
    /**
     * The stored draft these proposals belong to (ourteam-backend#4101).
     *
     * Absent until a draft has been generated, and it is what the publish call
     * writes from — no draft id, no publish. The sample workshop never sets it,
     * so a fixture page can be read and revised but never published, which is
     * the same "live-write is real-business-only" rule the rest of the module
     * follows.
     */
    draftId?: string;
    /** What kind of page this is — see PageRole. */
    role: PageRole;
    /** New links allowed on this page (see linkBudgetForRole). */
    linkBudget: number;
    /** The whole extracted page, in document order. Blocks with no
     *  recommendation render plain — the reviewer reads everything. */
    blocks: PageBlock[];
    recommendations: PageRecommendation[];
    history: PageHistoryEntry[];
    metrics: PageSearchMetrics | null;
    /**
     * The voice the drafts are written in, named explicitly. The brief is
     * firm that copy must "mimic the language used by the company" — naming
     * the source makes that checkable instead of taken on faith.
     */
    voice: { summary: string; sampledFrom: string[] };
}

function hashOf(s: string): number {
    return s.split("").reduce((a, c) => (a * 31 + c.charCodeAt(0)) % 99991, 7);
}

/** A deterministic, always-loadable stand-in for a crawled image — proves
 *  the real-src render path without depending on the network. */
function svgPlaceholder(label: string): string {
    const svg =
        `<svg xmlns='http://www.w3.org/2000/svg' width='640' height='300'>` +
        `<rect width='100%' height='100%' fill='#e8eef7'/>` +
        `<rect x='6' y='6' width='628' height='288' fill='none' stroke='#c7d4e8' stroke-width='2'/>` +
        `<text x='50%' y='52%' font-family='sans-serif' font-size='22' fill='#5b7397' text-anchor='middle'>${label}</text>` +
        `</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

const VOICE_SAMPLES = ["/about", "/services", "/contact"];

/**
 * Deterministic sample workshop — deliberately a MESSY, real-shaped page:
 * hero stats, a service card grid, a fee table, existing links, a
 * testimonial, a chart, real images. Replace with the real fetch once the
 * BE returns page content and the link graph.
 */
export function getPageWorkshop(url: string, title: string): PageWorkshop {
    const h = hashOf(url);
    const hasMetrics = h % 4 !== 0;

    const role = roleForUrl(url);

    return {
        url,
        title,
        role,
        linkBudget: linkBudgetForRole(role),
        metrics: hasMetrics
            ? {
                clicks: 40 + (h % 260),
                impressions: 2400 + (h % 9000),
                ctr: Number((0.8 + ((h % 40) / 10)).toFixed(1)),
                position: Number((3 + ((h % 140) / 10)).toFixed(1)),
            }
            : null,
        voice: {
            summary:
                "Plain, second person, short sentences. Names the service before the benefit and avoids superlatives.",
            sampledFrom: VOICE_SAMPLES,
        },
        history: [
            {
                id: "h1",
                kind: "drafted",
                at: "2026-07-28T09:12:00Z",
                by: "Oura",
                changes: [
                    {
                        kind: "copy",
                        where: "Opening line",
                        before: "Award winning care for your whole family, delivered by the best team in the region.",
                        after: "Family dentistry in Subang Jaya, with same day emergency visits.",
                    },
                    {
                        kind: "copy",
                        where: "What we do",
                        before: "We offer a wide range of treatments to suit every need and budget.",
                        after: "We handle check ups, fillings, veneers and Invisalign, with fees listed before you book.",
                    },
                    {
                        kind: "link",
                        where: "Questions we get asked",
                        anchorText: "walk ins",
                        targetUrl: "/contact",
                    },
                ],
            },
            {
                id: "h2",
                kind: "published",
                at: "2026-07-29T11:02:00Z",
                by: "Nadia Rahman",
                via: "WordPress",
                changes: [
                    {
                        kind: "copy",
                        where: "Opening line",
                        before: "Award winning care for your whole family, delivered by the best team in the region.",
                        after: "Family dentistry in Subang Jaya, with same day emergency visits.",
                    },
                    {
                        kind: "link",
                        where: "Questions we get asked",
                        anchorText: "walk ins",
                        targetUrl: "/contact",
                    },
                ],
            },
            {
                id: "h3",
                kind: "redrafted",
                at: "2026-08-02T14:38:00Z",
                by: "Nadia Rahman",
                instruction:
                    "Keep the opening line, it is the one thing the client liked. Cut the pricing claim.",
                changes: [
                    {
                        kind: "copy",
                        where: "What we do",
                        before: "We offer a wide range of treatments to suit every need and budget.",
                        after: "We handle check ups, scaling and polishing, fillings, veneers and Invisalign.",
                    },
                    {
                        kind: "link",
                        where: "Services list",
                        anchorText: "Invisalign clear aligners",
                        targetUrl: "/services/invisalign",
                    },
                    {
                        kind: "repoint",
                        where: "General dentistry card",
                        anchorText: "See pricing",
                        fromUrl: "/",
                        targetUrl: "/pricing",
                    },
                ],
            },
            // Two events on one day, so the grouping is visible.
            {
                id: "h4",
                kind: "published",
                at: "2026-08-04T02:05:00Z",
                by: "Nadia Rahman",
                via: "WordPress",
                changes: [
                    {
                        kind: "copy",
                        where: "What we do",
                        before: "We offer a wide range of treatments to suit every need and budget.",
                        after: "We handle check ups, scaling and polishing, fillings, veneers and Invisalign.",
                    },
                    {
                        kind: "link",
                        where: "Services list",
                        anchorText: "Invisalign clear aligners",
                        targetUrl: "/services/invisalign",
                    },
                    {
                        kind: "repoint",
                        where: "General dentistry card",
                        anchorText: "See pricing",
                        fromUrl: "/",
                        targetUrl: "/pricing",
                    },
                ],
            },
            {
                id: "h5",
                kind: "drafted",
                at: "2026-08-04T08:20:00Z",
                by: "Oura",
                changes: [
                    {
                        kind: "copy",
                        where: "Why patients choose us",
                        before: "modern equipment and gentle techniques",
                        after: "digital X-rays, 3D scanning and pain free local anaesthesia",
                    },
                    {
                        kind: "link",
                        where: "Why patients choose us",
                        anchorText: "fees are listed openly",
                        targetUrl: "/pricing",
                    },
                    {
                        kind: "related",
                        where: "After the fee table",
                        targetUrl: "/services/invisalign, /services/whitening, /about",
                    },
                ],
            },
        ],
        blocks: [
            { id: "b1", tag: "h1", text: "Welcome to SmileCraft Dental" },
            { id: "b2", tag: "p", text: "Award winning care for your whole family, delivered by the best team in the region." },
            {
                id: "b3",
                tag: "img",
                text: "",
                alt: "Reception area of the clinic",
                src: svgPlaceholder("Reception area photo"),
                figure: "photo",
            },
            // Hero counters — real sites love these; they extract as
            // value + label pairs, not prose.
            { id: "b4", tag: "stat", text: "12,400", label: "patients treated", group: "stats:hero" },
            { id: "b5", tag: "stat", text: "4.9 / 5", label: "Google rating", group: "stats:hero" },
            { id: "b6", tag: "stat", text: "15 years", label: "serving Subang Jaya", group: "stats:hero" },
            { id: "b7", tag: "h2", text: "What we do" },
            { id: "b8", tag: "p", text: "We offer a wide range of treatments to suit every need and budget." },
            { id: "b9", tag: "li", text: "Check ups, scaling and polishing" },
            { id: "b10", tag: "li", text: "Fillings and crowns" },
            { id: "b11", tag: "li", text: "Invisalign clear aligners" },
            { id: "b12", tag: "li", text: "Veneers and whitening" },
            {
                id: "b13",
                tag: "img",
                text: "",
                alt: "Treatment room with dental chair",
                src: svgPlaceholder("Treatment room photo"),
                figure: "photo",
            },
            { id: "b14", tag: "h2", text: "Our services" },
            // A card grid: three cards, each a repeated h3 + p sibling
            // structure — which is exactly how the DOM walk detects it.
            // Two of the cards carry links that ALREADY exist on the page.
            { id: "b15", tag: "h3", text: "General dentistry", group: "cards:services" },
            {
                id: "b16",
                tag: "p",
                // "See pricing" links at the HOMEPAGE — the wrong-target case
                // the build sheet flags. r8 repoints it.
                text: "Check ups, cleaning and fillings for the whole family, with reminders when your next visit is due. See pricing.",
                links: [{ text: "See pricing", href: "/" }],
                group: "cards:services",
            },
            { id: "b17", tag: "h3", text: "Cosmetic dentistry", group: "cards:services" },
            {
                id: "b18",
                tag: "p",
                text: "Veneers, whitening and smile makeovers, planned with you before any work starts. Browse the smile gallery.",
                links: [{ text: "Browse the smile gallery", href: "/gallery" }],
                group: "cards:services",
            },
            { id: "b19", tag: "h3", text: "Kids dentistry", group: "cards:services" },
            {
                id: "b20",
                tag: "p",
                text: "Gentle first visits for children from age three, in a room set up for them.",
                group: "cards:services",
            },
            {
                id: "b21",
                tag: "table",
                text: "Consultation fees",
                table: {
                    header: ["Treatment", "Price", "Duration"],
                    rows: [
                        ["Consultation", "RM 50", "20 min"],
                        ["Scaling and polishing", "RM 150", "45 min"],
                        ["Tooth filling", "from RM 180", "30 min"],
                        ["Invisalign consultation", "Free", "30 min"],
                    ],
                },
            },
            { id: "b22", tag: "h2", text: "Why patients choose us" },
            // TWO recommendations on one block (a copy change on one clause
            // plus a link on another) — the +N marker case.
            {
                id: "b23",
                tag: "p",
                text: "We use modern equipment and gentle techniques, and our fees are listed openly so there are no surprises when you visit.",
            },
            {
                id: "b24",
                tag: "quote",
                text: "The team explained every step before they did anything. My daughter actually looks forward to her check ups now.",
                label: "Amrita, patient since 2023",
            },
            // A chart is DATA drawn as pixels — the crawl can only carry its
            // alt/aria text, so the workshop shows exactly that, honestly.
            {
                id: "b25",
                tag: "img",
                text: "",
                alt: "Bar chart: patient satisfaction score by service, 2023 to 2026",
                figure: "chart",
            },
            { id: "b26", tag: "h2", text: "Meet the team" },
            { id: "b27", tag: "p", text: "Our dentists are friendly, professional and genuinely care about your comfort." },
            {
                id: "b28",
                tag: "p",
                text: "Every dentist here is registered with the Malaysian Dental Council.",
                links: [{ text: "Malaysian Dental Council", href: "https://www.mdc.gov.my" }],
            },
            { id: "b29", tag: "h2", text: "Questions we get asked" },
            { id: "b30", tag: "p", text: "Do you accept walk ins? Yes, subject to availability on the day." },
            { id: "b31", tag: "h2", text: "Opening hours" },
            { id: "b32", tag: "p", text: "Monday to Saturday, 9am to 6pm. Closed on public holidays." },
        ],
        recommendations: [
            {
                kind: "copy",
                id: "r1",
                blockId: "b2",
                before:
                    "Award winning care for your whole family, delivered by the best team in the region.",
                after:
                    "Family dentistry in Subang Jaya, from routine check ups to same day emergency visits.",
                justification: {
                    reason:
                        "The line claims a superlative and never says what the practice does or where it is. An answer engine cannot cite it for any question a patient actually asks, and neither can a search snippet.",
                    expected:
                        "The page becomes eligible for local intent queries it currently cannot match, because the service and the location are now stated in the first line.",
                },
            },
            {
                kind: "copy",
                id: "r2",
                blockId: "b8",
                before: "We offer a wide range of treatments to suit every need and budget.",
                after:
                    "We handle check ups, scaling and polishing, fillings, veneers and Invisalign, with fees listed before you book.",
                justification: {
                    reason:
                        "\"Wide range of treatments\" names nothing, so the page cannot be retrieved for any specific treatment. The five treatments named here are the ones the site already sells elsewhere.",
                    expected:
                        "Each named treatment becomes a phrase the page can be matched on, instead of one generic sentence that matches nothing.",
                },
            },
            {
                kind: "link",
                id: "r3",
                blockId: "b11",
                anchorText: "Invisalign clear aligners",
                targetUrl: "/services/invisalign",
                targetTitle: "Invisalign clear aligners",
                requiresNewCopy: false,
                // Two-way: the Invisalign page should point back here. That
                // half is an ordinary proposal on THAT page; this field only
                // tells the reviewer it exists.
                pairsWith: { url: "/services/invisalign", title: "Invisalign clear aligners" },
                justification: {
                    reason:
                        "The Invisalign page has no inbound links from anywhere on the site, and this list item already names the treatment. Linking it is the cheapest way to make that page reachable.",
                    expected:
                        "The Invisalign page stops being an orphan and starts receiving crawl paths from a page that already has traffic.",
                },
            },
            {
                kind: "link",
                id: "r4",
                blockId: "b27",
                anchorText: "book a first visit",
                targetUrl: "/contact",
                targetTitle: "Contact and booking",
                requiresNewCopy: true,
                proposedCopy:
                    "If you are new to the practice, you can book a first visit and we will talk through what you need before any treatment starts.",
                justification: {
                    reason:
                        "This part of the page has no sentence that could carry a link without forcing it. Adding one line gives the page a natural route to booking, which is the action this page exists to prompt.",
                    expected:
                        "A path from the team section to booking, which currently does not exist anywhere on this page.",
                },
            },
            {
                kind: "copy",
                id: "r5",
                blockId: "b30",
                before: "Do you accept walk ins? Yes, subject to availability on the day.",
                after:
                    "Do you accept walk ins? Yes. We keep two slots open each weekday morning for walk ins and same day emergencies. Call ahead if you can, and we will tell you the wait.",
                justification: {
                    reason:
                        "The answer is four words and gives a patient nothing to act on. Questions like this are exactly what answer engines quote, and a thin answer is passed over for a competitor's fuller one.",
                    expected:
                        "An answer complete enough to be quoted whole, rather than one an engine has to skip for lack of detail.",
                },
            },
            // r6 + r7 both live on b23 — a copy change on one clause and a
            // link on another. This is the multi-proposal marker case.
            {
                kind: "copy",
                id: "r6",
                blockId: "b23",
                before: "modern equipment and gentle techniques",
                after: "digital X-rays, 3D scanning and pain free local anaesthesia",
                justification: {
                    reason:
                        "\"Modern equipment\" is a claim every clinic makes and no engine can verify or match. The three named tools are the ones the site's own service pages already describe.",
                    expected:
                        "The sentence becomes retrievable for equipment-specific searches and reads as evidence instead of assertion.",
                },
            },
            {
                kind: "link",
                id: "r7",
                blockId: "b23",
                // The build sheet's caveat, modelled: the phrase was captured
                // as "our fees are listed clearly", the live page says
                // "fees are listed openly". The link goes on what is actually
                // there, and the card says so.
                anchorText: "our fees are listed clearly",
                match: "approximate",
                matchedText: "fees are listed openly",
                targetUrl: "/pricing",
                targetTitle: "Consultation fees and pricing",
                requiresNewCopy: false,
                justification: {
                    reason:
                        "The sentence promises open pricing but does not take the reader there, and the pricing page has only one inbound link on the whole site.",
                    expected:
                        "A second crawl path into the pricing page, from the exact words that promise it.",
                },
            },
            // An existing link with the wrong destination. The text does not
            // change, so this consumes none of the page's link budget.
            {
                kind: "repoint",
                id: "r8",
                blockId: "b16",
                anchorText: "See pricing",
                currentUrl: "/",
                targetUrl: "/pricing",
                targetTitle: "Consultation fees and pricing",
                justification: {
                    reason:
                        "The phrase promises pricing but the link goes to the homepage, so a reader who clicks it has to find the fees themselves and the pricing page gets none of the credit for the visit.",
                    expected:
                        "The click lands where the words said it would, and the pricing page gains an inbound link it should already have had.",
                },
            },
            // The no-natural-phrase case: a row of links, NOT rewritten copy.
            {
                kind: "related",
                id: "r9",
                blockId: "b21",
                label: "Related reading",
                links: [
                    { url: "/services/invisalign", title: "Invisalign clear aligners" },
                    { url: "/services/whitening", title: "Teeth whitening" },
                    { url: "/about", title: "About the practice" },
                ],
                justification: {
                    reason:
                        "Nothing in the fee table is a phrase that could carry a link without forcing it, and three service pages that belong with this content have no route in from here.",
                    expected:
                        "Three pages become reachable from a page that already has traffic, without touching a word of the existing copy.",
                },
            },
        ],
    };
}

/**
 * Internal links ALREADY in the page's body copy.
 *
 * These count toward the budget, because the rule they come from ("keep it to
 * two or three links per page so it reads naturally") is about how the
 * finished page reads — a reader cannot tell which links are new. External
 * links are excluded: they are not internal linking.
 */
export function existingInternalLinks(page: PageWorkshop): number {
    return page.blocks.reduce(
        (n, b) => n + (b.links ?? []).filter((l) => !/^https?:\/\//i.test(l.href)).length,
        0,
    );
}

/** New links this page's proposals would add. Repoints are excluded — they
 *  move an existing link rather than adding one. */
export function linksAdded(page: PageWorkshop): number {
    return page.recommendations.reduce((n, r) => {
        if (r.kind === "link") return n + 1;
        if (r.kind === "related") return n + r.links.length;
        return n;
    }, 0);
}

/**
 * One page's review so far: what the reviewer took out, and what she asked to
 * be redrafted. Held by the SCREEN, keyed by url, so following a two-way link
 * to another page and coming back does not throw the work away.
 */
export interface PageReview {
    /**
     * The proposals the reviewer has explicitly staged for publishing (#3546).
     *
     * This replaces a `dismissed` list, and the direction is the whole point.
     * Under opt-out, opening a page and pressing Publish wrote every proposal
     * to a client's live site, including the ones never read — the default did
     * the destructive thing. A missed inclusion costs one more click; a missed
     * exclusion is a wrong sentence on a client's production page.
     *
     * Stored reviews carrying the old `dismissed` key are NOT translated. Under
     * inclusion-by-default an empty `dismissed` meant "publish all nine"; read
     * as staging it would have to mean "publish none". Rather than pick, the old
     * key is dropped and the page opens with nothing staged, which is the safe
     * reading either way.
     */
    staged: string[];
    notes: Record<string, string>;
}

export const EMPTY_REVIEW: PageReview = { staged: [], notes: {} };

/** The recommendations that touch one block, in declaration order. */
export function recsForBlock(page: PageWorkshop, blockId: string): PageRecommendation[] {
    return page.recommendations.filter((r) => r.blockId === blockId);
}

export function countByKind(page: PageWorkshop): { copy: number; link: number } {
    return {
        copy: page.recommendations.filter((r) => r.kind === "copy").length,
        link: page.recommendations.filter((r) => r.kind === "link").length,
    };
}
